import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import genericHelpers from '../_utils/generic-helpers';
import { ShelfUI, builderDispatch } from '../_utils/ui';
import ColorFilterOptions from './color-filter-options';
import ImageFilterOptions from './image-filter-options';
import StandardFilterOptions from './standard-filter-options';
import DecapitalisedTitle from '../_utils/decapitalised-title';
// -----------------------------------------------------------
//  Filter
// -----------------------------------------------------------
function Filter({
  filter_name,
  multiselectable,
  modal,
  selected_options: selectedOptions,
  name,
  options,
  title,
  facets,
  category
}) {
  const [collapsed, setCollapsed] = useState(true);
  const [left, setLeft] = useState(0)
  const [fixLeft, setFixLeft] = useState(false);
  const componentRef = useRef(null);
  const buttonRef = useRef(null);
  let width;

  useEffect(() => {
    updateLeftPosition();
    width = componentRef.current && componentRef.current.offset + 1;
    window.addEventListener('click', handleClick);
    return () => window.removeEventListener('click', handleClick);
  }, []);

  const updateLeftPosition = () => {
    const viewWidth = window.innerWidth;
    const { left, width: menuWidth } =
      buttonRef.current.getBoundingClientRect();
    let menuLeft = left;
    if (left + menuWidth > viewWidth) {
      menuLeft = viewWidth - menuWidth;
    }
    menuLeft = Math.max(menuLeft, 7);
    setLeft(menuLeft);
    if (viewWidth < 1250) {
      setFixLeft(true)
    }
  }

  const handleClick = e => {
    if (componentRef.current && !componentRef.current.contains(e.target)) {
      setCollapsed(true);
    }
  };

  const handleCollapsedClicked = () => {
    updateLeftPosition();
    setCollapsed(prevState => !prevState);
  };

  const handleOptionClick = option => {
    builderDispatch.delayedDispatch({
      type: ShelfUI.FILTER_SELECTED,
      filter: filter_name,
      option: option,
      multiselectable: multiselectable,
    });
    if (!modal) setCollapsed(prevState => !prevState);
  };

  const filterTitle =
    selectedOptions.length > 0 ? `${title} (${selectedOptions.length})` : title;
  const isColor = () => {
    return ['color', 'colour'].some(term => name === term);
  }

  const isImage = () => {
    const option = options[0];

    return option && option.image;
  };

  const getOptions = () => {
    if (isColor())
      return (
        <ColorFilterOptions
          options={options}
          selected_options={selectedOptions}
          facets={facets}
          onOptionSelected={handleOptionClick}
          newUi={true}
        />
      );

    if (isImage())
      return (
        <ImageFilterOptions
          category={category}
          options={options}
          selected_options={selectedOptions}
          facets={facets}
          onOptionSelected={handleOptionClick}
          newUi={true}
        />
      );

    return (
      <StandardFilterOptions
        options={options}
        selected_options={selectedOptions}
        facets={facets}
        onOptionSelected={handleOptionClick}
        newUi={true}
      />
    );
  };

  return (
    <li
      className={classNames(
        'm-1',
        { 'modal-li': modal },
        { 'selected-li': selectedOptions.length > 0 }
      )}
      ref={componentRef}>
      <div className='options-wrapper d-flex flex-column'>
        <button
          aria-expanded={!collapsed}
          className={classNames('btn shelf-filter-dropdown-toggle', {
            'selected-button': selectedOptions.length > 0,
            'modal-button d-flex d-flex-row justify-content-between': modal,
          })}
          onClick={handleCollapsedClicked}
          ref={buttonRef}>
          <DecapitalisedTitle>
            {genericHelpers.tidyTitleFilter(filterTitle)}
          </DecapitalisedTitle>

          <i
            className={classNames(
              'fa ml-2',
              collapsed ? 'fa-angle-down' : 'fa-angle-up'
            )}></i>
        </button>
        <div
          role="menu"
          aria-hidden={collapsed}
          aria-label={title}
          className={classNames('shelf-filter-dropdown px-4', {
            'd-none': collapsed,
            'colour-menu': isColor(),
            'image-filters-dropdown': isImage(),
            'py-3': !modal,
          })}
          style={fixLeft ? { left: left } : {}}>
          {getOptions()}
          {selectedOptions.length > 0 && (
            <button
              className={classNames('btn btn-link p-0', {
                'clear-button': true,
              })}
              onClick={() => {
                handleOptionClick(null);
              }}>
              Clear
            </button>
          )}
        </div>
      </div>
    </li>
  );
}

Filter.propTypes = {
  filter_name: PropTypes.string,
  name: PropTypes.string,
  title: PropTypes.string,
  options: PropTypes.array,
  category: PropTypes.string,
  facets: PropTypes.array,
  selected_ptions: PropTypes.array,
  modal: PropTypes.bool
};

Filter.defaultProps = {
  modal: null
};

export default Filter;
